import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faPlus, faClock, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import toast from 'react-hot-toast';
import ConfirmationModal from "./ConfirmationModal";

// Difficulty → pill style
const getDifficultyStyle = (difficulty) => {
  if (difficulty === "Easy") return "bg-green-50 text-green-700 border border-green-200";
  if (difficulty === "Medium") return "bg-amber-50 text-amber-700 border border-amber-200";
  return "bg-red-50 text-red-700 border border-red-200";
};

const AssessmentCategoryCards = ({
  selectedLevel = null,
  allCategories = [],
  onConfirm = null,
  onCategoriesChange = null,
}) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    category_id: "",
    category_name: "",
    questions_count: "",
    duration: "30",
    difficulty: "Easy",
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    if (selectedLevel?.categories) {
      setSelectedCategories(selectedLevel.categories);
    } else {
      setSelectedCategories([]);
    }
  }, [selectedLevel]);

  const handleEdit = (category) => {
    setCurrentCategory({ ...category });
    setIsEditModalOpen(true);
  };

  const handleDelete = (category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!categoryToDelete) return;
    const updatedCategories = selectedCategories.filter(
      (cat) => cat.id !== categoryToDelete.id
    );
    setSelectedCategories(updatedCategories);
    if (onCategoriesChange) onCategoriesChange(updatedCategories);
    toast.success("Category removed");
    setIsDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  const handleUpdate = () => {
    if (!currentCategory.questions_count || !currentCategory.duration) {
      toast.error("Please fill all fields");
      return;
    }
    const updatedCategories = selectedCategories.map((cat) =>
      cat.id === currentCategory.id ? currentCategory : cat
    );
    setSelectedCategories(updatedCategories);
    if (onCategoriesChange) onCategoriesChange(updatedCategories);
    setIsEditModalOpen(false);
    setCurrentCategory(null);
    toast.success("Category updated");
  };

  const handleAddCategory = () => {
    if (!newCategory.category_id || !newCategory.questions_count || !newCategory.duration) {
      toast.error("Please select a category and fill all fields");
      return;
    }
    const selectedCat = allCategories.find(
      (cat) => cat.id === parseInt(newCategory.category_id)
    );
    if (!selectedCat) {
      toast.error("Category not found");
      return;
    }
    const categoryToAdd = {
      id: `new-${selectedCat.id}-${Date.now()}`,
      category_id: selectedCat.id,
      category_name: selectedCat.category_name,
      questions_count: parseInt(newCategory.questions_count),
      duration: newCategory.duration,
      difficulty: newCategory.difficulty,
    };
    const updatedCategories = [...selectedCategories, categoryToAdd];
    setSelectedCategories(updatedCategories);
    if (onCategoriesChange) onCategoriesChange(updatedCategories);
    setIsAddModalOpen(false);
    setNewCategory({ category_id: "", category_name: "", questions_count: "", duration: "30", difficulty: "Easy" });
    toast.success("Category added");
  };

  if (!selectedLevel) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-campus-text-body text-sm">Select an Assessment level to configure categories</p>
      </div>
    );
  }

  // shared modal input class
  const inputClass = "w-full px-3 py-2 text-sm border border-campus-border rounded-lg bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all";
  const labelClass = "block text-xs font-semibold text-campus-text-body uppercase tracking-wide mb-1.5";

  return (
    <div className="p-5">

      {/* ── Section Header ───────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-base font-heading font-bold text-campus-text-primary">Assessment Configuration</h2>
          <p className="text-xs text-campus-text-body mt-0.5">
            Configuring: <span className="font-semibold text-campus-text-primary">{selectedLevel.level_name}</span>
          </p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center gap-2 bg-campus-primary hover:bg-campus-primary-dark text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm whitespace-nowrap"
        >
          <FontAwesomeIcon icon={faPlus} className="text-xs" />
          Add Category
        </button>
      </div>

      {/* ── Category Cards ────────────────────────────────────────────────── */}
      {selectedCategories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 bg-campus-bg rounded-xl border-2 border-dashed border-campus-border">
          <div className="w-10 h-10 rounded-full bg-campus-surface border border-campus-border flex items-center justify-center mb-3">
            <FontAwesomeIcon icon={faLayerGroup} className="text-campus-text-body text-sm" />
          </div>
          <p className="text-sm font-medium text-campus-text-body">No categories configured yet</p>
          <p className="text-xs text-campus-text-body mt-1">Click "Add Category" to get started</p>
        </div>
      ) : (
        <div className={`grid gap-4 ${selectedCategories.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
          {selectedCategories.map((category, index) => (
            <div
              key={`${category.id}-${index}`}
              className="group bg-campus-surface border border-campus-border rounded-xl p-4 hover:shadow-card transition-all duration-200 relative"
            >
              {/* Top row: name + actions */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="font-semibold text-campus-text-primary text-sm leading-snug">{category.category_name}</h3>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(category)}
                    className="p-1.5 rounded-lg hover:bg-campus-bg text-campus-text-body hover:text-campus-primary transition-colors"
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} className="text-xs" />
                  </button>
                  <button
                    onClick={() => handleDelete(category)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-campus-text-body hover:text-campus-error transition-colors"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} className="text-xs" />
                  </button>
                </div>
              </div>

              {/* Pill tags row */}
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                  <FontAwesomeIcon icon={faClock} className="text-[10px]" />
                  {category.duration} mins
                </span>
                <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${getDifficultyStyle(category.difficulty)}`}>
                  {category.difficulty}
                </span>
                <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-campus-bg text-campus-text-primary border border-campus-border">
                  {category.questions_count} Qs
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Edit Modal ───────────────────────────────────────────────────── */}
      {isEditModalOpen && currentCategory && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50" style={{ zIndex: 9999 }}>
          <div className="bg-campus-surface rounded-2xl shadow-hover max-w-md w-full mx-4">
            <div className="p-5 border-b border-campus-border">
              <h3 className="text-base font-heading font-bold text-campus-text-primary">Edit Category</h3>
              <p className="text-xs text-campus-text-body mt-0.5">{currentCategory.category_name}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label htmlFor="edit-category-name" className={labelClass}>Category Name</label>
                <input
                  id="edit-category-name"
                  type="text"
                  value={currentCategory.category_name}
                  disabled
                  className={`${inputClass} opacity-60 cursor-not-allowed`}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="edit-duration" className={labelClass}>Duration (mins)</label>
                  <input
                    id="edit-duration"
                    type="number"
                    value={currentCategory.duration}
                    onChange={(e) => setCurrentCategory({ ...currentCategory, duration: e.target.value })}
                    className={inputClass}
                    placeholder="30"
                  />
                </div>
                <div>
                  <label htmlFor="edit-questions-count" className={labelClass}>Questions Count</label>
                  <input
                    id="edit-questions-count"
                    type="number"
                    min="1"
                    max="100"
                    value={currentCategory.questions_count}
                    onChange={(e) => setCurrentCategory({ ...currentCategory, questions_count: e.target.value })}
                    className={inputClass}
                    placeholder="10"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="edit-difficulty" className={labelClass}>Difficulty</label>
                <select
                  id="edit-difficulty"
                  value={currentCategory.difficulty}
                  onChange={(e) => setCurrentCategory({ ...currentCategory, difficulty: e.target.value })}
                  className={inputClass}
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>
            <div className="p-5 border-t border-campus-border flex justify-end gap-2">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-sm border border-campus-border text-campus-text-body rounded-lg hover:bg-campus-bg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 text-sm bg-campus-primary hover:bg-campus-primary-dark text-white rounded-lg transition-colors font-semibold"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Modal ────────────────────────────────────────────────────── */}
      {isAddModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50" style={{ zIndex: 9999 }}>
          <div className="bg-campus-surface rounded-2xl shadow-hover max-w-md w-full mx-4">
            <div className="p-5 border-b border-campus-border">
              <h3 className="text-base font-heading font-bold text-campus-text-primary">Add Category</h3>
              <p className="text-xs text-campus-text-body mt-0.5">Configure a new question category for this assessment</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label htmlFor="add-category-name" className={labelClass}>Category</label>
                <select
                  id="add-category-name"
                  value={newCategory.category_id}
                  onChange={(e) => {
                    const selectedCat = allCategories.find((cat) => cat.id === parseInt(e.target.value));
                    setNewCategory({ ...newCategory, category_id: e.target.value, category_name: selectedCat ? selectedCat.category_name : "" });
                  }}
                  className={inputClass}
                >
                  <option value="">— Select a category —</option>
                  {allCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="add-duration" className={labelClass}>Duration (mins)</label>
                  <input
                    id="add-duration"
                    type="number"
                    value={newCategory.duration}
                    onChange={(e) => setNewCategory({ ...newCategory, duration: e.target.value })}
                    className={inputClass}
                    placeholder="30"
                  />
                </div>
                <div>
                  <label htmlFor="add-questions-count" className={labelClass}>Questions Count</label>
                  <input
                    id="add-questions-count"
                    type="number"
                    min="1"
                    max="100"
                    value={newCategory.questions_count}
                    onChange={(e) => setNewCategory({ ...newCategory, questions_count: e.target.value })}
                    className={inputClass}
                    placeholder="10"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="add-difficulty" className={labelClass}>Difficulty</label>
                <select
                  id="add-difficulty"
                  value={newCategory.difficulty}
                  onChange={(e) => setNewCategory({ ...newCategory, difficulty: e.target.value })}
                  className={inputClass}
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>
            <div className="p-5 border-t border-campus-border flex justify-end gap-2">
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="px-4 py-2 text-sm border border-campus-border text-campus-text-body rounded-lg hover:bg-campus-bg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                className="px-4 py-2 text-sm bg-campus-primary hover:bg-campus-primary-dark text-white rounded-lg transition-colors font-semibold"
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation ──────────────────────────────────────────── */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Delete Category"
        message="Are you sure you want to remove this category from the assessment?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-campus-primary hover:bg-campus-primary-dark"
      />
    </div>
  );
};

AssessmentCategoryCards.propTypes = {
  selectedLevel: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    level_name: PropTypes.string,
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category_name: PropTypes.string,
        questions_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        difficulty: PropTypes.string,
      })
    ),
  }),
  allCategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      category_name: PropTypes.string,
    })
  ),
  onConfirm: PropTypes.func,
  onCategoriesChange: PropTypes.func,
};

export default AssessmentCategoryCards;
